import React, { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@apollo/client";
import { GET_CUSTOMER_INVOICES_BY_DATE } from "src/api/invoice";
import {
    GetCustomerInvoicesByDate,
    GetCustomerInvoicesByDateVariables
} from "src/api/generated/GetCustomerInvoicesByDate";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import { withRouter, RouteComponentProps } from "react-router";
import { getDateForHasuraQuery, getDateWithMonthAndYear } from "src/utils/Date";
import InvoiceDetailsList from "src/screens/services/costsAndInvoices/invoiceDetailsList/InvoiceDetailsList";
import { useParams } from "react-router";
import { Route, selectRoute } from "src/config/routes";
import { InvoiceWithCostType } from "src/screens/services/costsAndInvoices/currentMonth/ServicesCostsAndInvoicesCurrentMonth";

export interface InvoiceDetail {
    yearAndMonth: string;
    amount: number;
    invoiced: string;
}
interface Params {
    year: string;
    month: string;
}

const ServicesCostsAndInvoicesHistoryInvoiceDetailsBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { navStore, authStore, uiSidebarStore } = useContext(RootStoreContext);

    const { year, month } = useParams<Params>();
    const lastDayOfMonth = new Date(Number(year), Number(month), 0).toString();

    const { data, loading, error } = useQuery<GetCustomerInvoicesByDate, GetCustomerInvoicesByDateVariables>(
        GET_CUSTOMER_INVOICES_BY_DATE,
        {
            variables: {
                date: getDateForHasuraQuery(lastDayOfMonth),
            }
        }
    );

    useEffect(() => {
        navStore.setTitle("");
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);

        navStore.setBackbutton(
            selectRoute(Route.servicesCostsAndInvoicesHistory, authStore.user?.role),
            t("screens.services.costs_and_invoices.history.title").toString()
        );
    }, [navStore, t, authStore.user?.role, uiSidebarStore.isSidebarActive]);

    if (loading) {
        return <Screens.Loading />;
    }
    if (error) {
        return <Screens.Error message={error.message} networkError={error.networkError} />;
    }
    if (!data) {
        return <Screens.Error message={t("error.nodata").toString()} />;
    }

    let customerInvoiceItems: InvoiceWithCostType[] = [];
    let totalOfAllInvoices: number = 0;

    data.customerinvoices.forEach((invoice) => {
        const count = invoice.count ?? 0;
        const total = invoice.costperunit * count;
        const customerInvoiceItem: InvoiceWithCostType = {
            customerInvoiceId: invoice.id,
            costType: invoice.costtype ?? 0,
            count: count,
            costPerUnit: invoice.costperunit,
            total: total,
            isTotalRow: false,
            countUserInvoiced: invoice.customerinvoiceusers_aggregate.aggregate?.count ?? 0
        };

        totalOfAllInvoices += total;
        customerInvoiceItems.push(customerInvoiceItem);
    });

    if (data.customerinvoices.length > 0) {
        customerInvoiceItems.push({
            total: totalOfAllInvoices,
            isTotalRow: true,
            countUserInvoiced: 0
        });
    }

    return (
        <React.Fragment>
            <UI.Container className="pt-4">
                <UI.Title>{getDateWithMonthAndYear(lastDayOfMonth)}</UI.Title>
                <UI.Row>
                    <UI.Col md={12}>
                        <UI.Card>
                            <div>
                                <InvoiceDetailsList invoiceItems={customerInvoiceItems} />
                            </div>
                        </UI.Card>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </React.Fragment>
    );
};

export const ServicesCostsAndInvoicesHistoryInvoiceDetails = withRouter(
    Sentry.withProfiler(observer(ServicesCostsAndInvoicesHistoryInvoiceDetailsBase))
);
